import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  getVendorContactPerson,
  getVendorEmailAddress,
} from 'src/app/modules/shared/utils/format-form-data-for-display';
import {
  MyPurchaseForm,
  UserInformation,
} from 'src/app/shared/models/app-models';

export interface Vendor {
  vendorName: string;
  contactPerson: string;
  vendorNumber: string;
  country: string;
  email: string;
  isDisabled: boolean;
}

type VendorNumbers = Vendor['vendorNumber'][];
type OptionalVendorNumbers = VendorNumbers | undefined;

export enum SelectVendorTableAction {
  EDIT,
  DELETE,
}

export enum SelectVendorTableSelectionMode {
  MULTIPLE = 'MULTIPLE',
  SINGLE = 'SINGLE'
}

@Component({
  selector: 'app-select-vendor-table',
  templateUrl: './select-vendor-table.component.html',
  styleUrls: ['./select-vendor-table.component.scss'],
})
export class SelectVendorTableComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() isScrollWithinTable: boolean = false;
  @Input() selectedVendorNumbers: VendorNumbers = [];
  @Output() onSelectedChange = new EventEmitter<VendorNumbers>();
  @Input() actionsType?: SelectVendorTableAction[];
  @Input() selectionMode: SelectVendorTableSelectionMode = SelectVendorTableSelectionMode.MULTIPLE;
  @Output() onRowEdit = new EventEmitter<Vendor>();
  @Output() onRowDelete = new EventEmitter<Vendor>();

  private _filterVendorNumbers: OptionalVendorNumbers;
  @Input()
  set filterVendorNumbers(value: OptionalVendorNumbers) {
    this._filterVendorNumbers = value;
    // Convert @Input to observable
    // Check for value change, else infinite loop
    if (!_.isEqual(this.filterVendorNumbersSubject.value, value)) {
      this.filterVendorNumbersSubject.next(value);
    }
  }
  get filterVendorNumbers(): OptionalVendorNumbers {
    return this._filterVendorNumbers;
  }
  private filterVendorNumbersSubject =
    new BehaviorSubject<OptionalVendorNumbers>(undefined);
  private filterVendorNumbers$ = this.filterVendorNumbersSubject.asObservable();

  @Input() isRemoveRowOnFilter?: boolean;

  vendorDataSource: MatTableDataSource<Vendor> = new MatTableDataSource<Vendor>(
    []
  );
  vendorDisplayColumns = [
    'select',
    'vendorName',
    'contactPerson',
    'vendorNumber',
    'country',
    'email',
    'action',
  ];
  pageSizeOptions: number[] = [5, 10, 20];
  pageSizeUniqueKey = 'pageSize-SelectVendorTable-vendor';
  defaultPageSize = 5;

  private vendorPaginator!: MatPaginator;

  @ViewChild('vendorPaginator', { read: MatPaginator })
  set matVendorPaginator(mp: MatPaginator) {
    this.vendorPaginator = mp;
    this.vendorDataSource.paginator = this.vendorPaginator;
  }

  private vendorSort!: MatSort;
  @ViewChild('vendorTableSort', { read: MatSort })
  set matVendorSort(ms: MatSort) {
    this.vendorSort = ms;
    this.vendorDataSource.sort = this.vendorSort;
  }

  constructor(
    private store: Store<{ user: UserInformation; form: MyPurchaseForm }>
  ) {
    const storedPageSize = localStorage.getItem(this.pageSizeUniqueKey);
    if (storedPageSize) this.defaultPageSize = Number(storedPageSize);
  }

  ngOnInit(): void {
    combineLatest([this.store.select('form'), this.filterVendorNumbers$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([form, filterVendorNumbers]: [
          MyPurchaseForm,
          OptionalVendorNumbers
        ]) => {
          if (form.documentNo) {
            let vendorsList = form.VendorsToSource;

            if (
              this.isRemoveRowOnFilter &&
              Array.isArray(filterVendorNumbers)
            ) {
              vendorsList = vendorsList.filter(
                (vendor) =>
                  vendor.VendorNumber &&
                  filterVendorNumbers.includes(vendor.VendorNumber)
              );
            }

            const formattedVendorList = this.formatVendorList(
              vendorsList,
              filterVendorNumbers
            );
            this.vendorDataSource.data = formattedVendorList;
          }
        }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private formatVendorList(
    vendorsList: MyPurchaseForm['VendorsToSource'],
    filterVendorNumbers: OptionalVendorNumbers
  ) {
    const formattedVendorsList = vendorsList
      .filter((vendor) => {
        const { isDeleted, Strikethrough } = vendor;
        return !isDeleted && !Strikethrough;
      })
      .map((vendor) => {
        const { Name, VendorNumber, AddressCountryCode } = vendor;

        const formattedVendor: Vendor = {
          vendorName: Name ?? '',
          contactPerson: getVendorContactPerson(vendor),
          vendorNumber: VendorNumber ?? '',
          country: AddressCountryCode ?? '',
          email: getVendorEmailAddress(vendor),
          isDisabled: Array.isArray(filterVendorNumbers)
            ? VendorNumber
              ? !filterVendorNumbers.includes(VendorNumber)
              : true
            : false,
        };
        return formattedVendor;
      });

    return formattedVendorsList;
  }

  getNonDisabled(list: Vendor[]) {
    return list.filter((row) => !row.isDisabled);
  }

  isAllVendorsSelected() {
    return this.getNonDisabled(this.vendorDataSource.data).every((vendor) =>
      this.selectedVendorNumbers.includes(vendor.vendorNumber)
    );
  }

  toggleAllVendorsRows() {
    if (this.isAllVendorsSelected()) {
      // Clear selection
      this.onSelectedChange.emit([]);
      return;
    }

    // Select all non-disabled
    this.onSelectedChange.emit(
      this.getNonDisabled(this.vendorDataSource.data).map(
        (vendor) => vendor.vendorNumber
      )
    );
  }

  toggleVendorRow(vendor: Vendor) {
    if (vendor.isDisabled) {
      return;
    }

    const { vendorNumber } = vendor;
    const isSelected = this.isVendorSelected(vendor);

    if (isSelected) {
      // De-select row
      this.onSelectedChange.emit(
        this.selectedVendorNumbers.filter(
          (selectedVendorNumber) => selectedVendorNumber !== vendorNumber
        )
      );
    } else {
      // Select row
      this.onSelectedChange.emit([...this.selectedVendorNumbers, vendorNumber]);
    }
  }

  onSelectSingleVendorRow(vendor:Vendor) {
    if (vendor.isDisabled) {
      return;
    }
    
    this.onSelectedChange.emit([vendor.vendorNumber])
  }

  isVendorSelected(vendor: Vendor) {
    const { vendorNumber } = vendor;
    return this.selectedVendorNumbers.includes(vendorNumber);
  }

  hasSelectedVendor() {
    return this.selectedVendorNumbers.length > 0;
  }

  onRowEditClick($event: Vendor) {
    this.onRowEdit.emit($event);
  }

  onRowDeleteClick($event: Vendor) {
    this.onRowDelete.emit($event);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.vendorDataSource.filter = filterValue.trim().toLowerCase();

    if (this.vendorDataSource.paginator) {
      this.vendorDataSource.paginator.firstPage();
    }
  }

  public onPaginatorPage(event: PageEvent) {
    const { pageSize } = event;

    localStorage.setItem(this.pageSizeUniqueKey, pageSize.toString());
  }

  public get SelectVendorTableAction(): typeof SelectVendorTableAction {
    return SelectVendorTableAction;
  }

  public get SelectVendorTableSelectionMode(): typeof SelectVendorTableSelectionMode {
    return SelectVendorTableSelectionMode;
  }
}
